import React, { FC, MouseEvent, ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form, Modal } from 'antd';
import { FieldData } from 'rc-field-form/lib/interface';

// Shared UI Library Components
import {
  ChipsInput,
  CustomButton,
  CustomIcon,
  CustomInput, CustomTooltip,
  defaultValidateMessages,
  DropdownElement,
  EIconName,
  emailPattern,
  SpecialtyAdding, stringPattern
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './add-counsellor-modal.scss';

// Components
import { CountryOption } from '../../shared/components/country-option/country-option';
import { SameCounsellorEmailModal } from '../same-counsellor-email-modal/same-counsellor-email-modal';

// Store entities
import { createCounsellor } from '../../store/counsellors/counsellors.thunks';
import { makeMeACounsellor } from '../../store/user/user.thunks';
import { fetchSpecializations } from '../../store/core/core.thunks';
import { getCoreState } from '../../store/core/core.selectors';
import { getUserState } from '../../store/user/user.selectors';

// Models
import { ICounsellor, ICounsellorCreate } from '../../interfaces/counsellors.interfaces';
import { ICountry, ISpeciality } from '../../interfaces/core.interfaces';

interface IAddCounsellorModalProps {
  open: boolean;
  initialData?: {
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  isCompanyOwner?: boolean;
  onSubmit?: (counsellor: ICounsellor) => void;
  onCancel?: () => void;
}

export const AddCounsellorModal: FC<IAddCounsellorModalProps> = (
  {
    open = false,
    initialData = null,
    isCompanyOwner = false,
    onSubmit = () => {
    },
    onCancel = () => {
    }
  }: IAddCounsellorModalProps
) => {
  const [form] = Form.useForm();
  const [btnDisabled, setBtnDisabled] = useState<boolean>(true);
  const [creatingCounsellor, setCreatingCounsellor] = useState<boolean>(false);
  const [sameCounsellorEmailOpen, setSameCounsellorEmailOpen] = useState<boolean>(false);
  const [companyOwner, setCompanyOwner] = useState<boolean>(isCompanyOwner);

  const {
    languages,
    countries,
    speciality
  } = useSelector(getCoreState);

  const dispatch = useDispatch<any>();

  const { user } = useSelector(getUserState);

  let specialtyData: { specialities: number[], specializations: string[] } = { specialities: [], specializations: [] };

  const toggleSameCounsellorEmailModal = (): void => {
    if (sameCounsellorEmailOpen) {
      form.setFieldValue('email', '');
    }
    setSameCounsellorEmailOpen(prevState => !prevState);
  };

  const onUseMakeMeACounsellorInfo = (): void => {
    toggleSameCounsellorEmailModal();
    onMakeMeACounsellor();
  };

  const handleCancel = (_: MouseEvent<HTMLButtonElement>): void => {
    setCompanyOwner(false);
    setBtnDisabled(true);
    form.resetFields();
    onCancel();
  };

  const generateOptions = (options: { name: string, id?: number, code?: string }[], isCountry: boolean = false): {
    label: ReactNode,
    value: string,
    disabled?: boolean,
  }[] => {
    return options.map((option) => (
      { label: isCountry ? <CountryOption country={ option as ICountry }/> : option.name, value: option.name })
    )
  };

  const languagesOptions = generateOptions(languages.list);
  const countriesOptions = generateOptions(countries.list, true);

  const generateOptionsIdArray = (
    options: { name: string, id?: number, code?: string }[], names: string[], mapKey: 'code' | 'id' = 'code'
  ): Array<string | number> => {
    return options
      .filter((opt) => names?.includes(opt?.name))
      .map((opt) => opt[mapKey] ? opt[mapKey] as string | number : '');
  };

  const handleSubmit = async (): Promise<void> => {
    setCreatingCounsellor(true);
    const {
      first_name,
      last_name,
      email,
      country_code,
      languages: languagesNames
    } = form.getFieldsValue();

    const counsellor: ICounsellorCreate = {
      first_name,
      last_name,
      email,
      languages: generateOptionsIdArray(languages.list, languagesNames),
      country_code: countries.list.find(c => c.name === country_code)?.code ?? '',
      ...specialtyData
    }

    const { payload } = await dispatch(
      companyOwner ? makeMeACounsellor(counsellor) : createCounsellor({ counsellor })
    );

    setCreatingCounsellor(false);

    if (payload) {
      onSubmit(payload);
    }
  }

  const onFieldsChange = (_: FieldData[], [first_name, last_name, email]: FieldData[]): void => {
    if (
      !companyOwner &&
      (email.value || '').toLowerCase() === (user?.email || '').toLowerCase()
    ) {
      toggleSameCounsellorEmailModal();
      return;
    }

    setBtnDisabled(
      (first_name.value?.length > 30 || !first_name.value?.length || !!first_name.errors?.length) ||
      (last_name.value?.length > 30 || !last_name.value?.length || !!last_name.errors?.length) ||
      (email.value?.length > 60 || !email.value?.length || !!email.errors?.length)
    );
  }

  const fetchSpecializationsHandler = ({ speciality, withoutDispatch }: {
    speciality: ISpeciality[],
    withoutDispatch: boolean
  }): Promise<any> => {
    return dispatch(fetchSpecializations({ speciality, withoutDispatch }));
  }

  const changeSpecialtyInfoHandler = (data: { specialities: number[], specializations: string[] }): void => {
    specialtyData = data;
  }

  const onMakeMeACounsellor = () => {
    setCompanyOwner(true);
    form.setFieldsValue({
      first_name: user?.first_name ?? '',
      last_name: user?.last_name ?? '',
      email: user?.email ?? ''
    });
    setBtnDisabled(false);
  }

  useEffect(() => {
    if (!initialData) return;
    form.setFieldsValue(initialData);
    setBtnDisabled(false);
  }, [initialData]);

  return (
    <>
      <SameCounsellorEmailModal open={ sameCounsellorEmailOpen }
                                onCancel={ toggleSameCounsellorEmailModal }
                                onOk={ onUseMakeMeACounsellorInfo }
      />
      <Modal
        rootClassName="add-counsellor-modal"
        width={ '814px' }
        open={ open }
        centered={ true }
        closeIcon={ false }
        title={ 'Add Consultant' }
        footer={ [
          <CustomButton key="cancel" text={ 'Cancel' } type={ 'text' } onClick={ handleCancel }/>,
          <CustomButton key="submit" text={ 'Add' } disabled={ btnDisabled || creatingCounsellor }
                        loading={ creatingCounsellor } onClick={ handleSubmit }/>
        ] }
        onCancel={ handleCancel }
      >
        { user?.is_counsellor === false && !isCompanyOwner && !companyOwner && <div className="make-counselor-action">
          <CustomButton type={ 'primary' } onClick={ onMakeMeACounsellor } text={ 'Make Me a Consultant' }
                        size={ 'small' }/>
        </div> }

        <Form className="add-counsellor-modal--body"
              form={ form }
              onFieldsChange={ onFieldsChange }
        >

          <div className="body-item">
            <div className="body-item--title">
              Please fill in all fields to add a new Consultant. Click "Add" to submit. Your added Consultant will appear
              in
              the "Consultants" section, where you can complete any necessary profile settings.
            </div>
          </div>
          <div className="body-item body-item--flex-items">
            <Form.Item
              name="first_name"
              label={ 'first name' }
              messageVariables={ { fieldLabel: 'First name' } }
              rules={ [
                { required: true, max: 30 },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || new RegExp(stringPattern).test(getFieldValue('first_name'))) {
                      return Promise.resolve();
                    }
                    if (value.length > 30) {
                      return Promise.resolve();
                    }
                    setBtnDisabled(true);
                    return Promise.reject(new Error(defaultValidateMessages.invalidFirstNameFormat));
                  }
                })
              ] }>
              <CustomInput type={ 'text' } placeholder={ 'First Name*' }/>
            </Form.Item>
            <Form.Item
              name="last_name"
              label={ 'last name' }
              messageVariables={ { fieldLabel: 'Last name' } }
              rules={ [
                { required: true, max: 30 },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || new RegExp(stringPattern).test(getFieldValue('last_name'))) {
                      return Promise.resolve();
                    }
                    if (value.length > 30) {
                      return Promise.resolve();
                    }
                    setBtnDisabled(true);
                    return Promise.reject(new Error(defaultValidateMessages.invalidLastNameFormat));
                  }
                })
              ] }>
              <CustomInput type={ 'text' } placeholder={ 'Last Name*' }/>
            </Form.Item>
          </div>
          <div className="body-item body-item--flex-items with-icon">
            <Form.Item
              name="email"
              label={ 'email' }
              messageVariables={ { fieldLabel: 'Email' } }
              rules={ [
                { required: true },
                { max: 60 },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || new RegExp(emailPattern).test(getFieldValue('email'))) {
                      return Promise.resolve();
                    }
                    if (value.length > 60) {
                      return Promise.resolve();
                    }
                    setBtnDisabled(true);
                    return Promise.reject(new Error(defaultValidateMessages.invalidEmailFormat));
                  }
                })] }>
              <CustomInput type={ 'email' } shouldTrim={ true } placeholder={ 'Email*' }
                           disabled={ isCompanyOwner || companyOwner }
                           suffix={ <CustomIcon name={ EIconName.Mail }/> }/>
            </Form.Item>
            <CustomTooltip placement={ 'bottomLeft' } trigger={ 'hover' }
                           title={ 'It is recommended to use only corporate communication channels.' }>
              <span className="info-icon-wrapper"><CustomIcon className="info-icon" name={ EIconName.Info }/></span>
            </CustomTooltip>
            <Form.Item name="country_code">
              <DropdownElement options={ countriesOptions }
                               withoutAll={ true }
                               showSearch={ true }
                               placeholder={ 'Country' }/>
            </Form.Item>
          </div>
          <div className="body-item">
            <Form.Item name="languages">
              <ChipsInput options={ languagesOptions }
                          placeholder={ 'Language(s)' }
                          hint={ 'Start typing to add a language' }
              />
            </Form.Item>
          </div>
          <div className="body-item body-item--flex-items">
            <SpecialtyAdding specialityList={ speciality.list } fetchSpecializations={ fetchSpecializationsHandler }
                             changeSpecialtyInfo={ changeSpecialtyInfoHandler }/>
          </div>
        </Form>
      </Modal>
    </>
  )
};
